import React, { useEffect, useState } from 'react';
import molecularService from '../../services/molecularService';
import MolecularViewer from './MolecularViewer';
import PhytochemistryMap from './PhytochemistryMap';

/**
 * PlantMolecularTab
 *
 * Tab content for the medicinal-plant detail modal. Lists curated molecular
 * targets relevant to the plant's category and renders a 3D viewer for the
 * one the user selects (defaults to the first).
 */
const PlantMolecularTab = ({ plantId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUniprot, setSelectedUniprot] = useState(null);

  useEffect(() => {
    if (!plantId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = await molecularService.getPlantTargets(plantId);
        if (cancelled) return;
        setData(payload);
        if (payload.targets && payload.targets.length > 0) {
          setSelectedUniprot(payload.targets[0].uniprot_id);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || 'No se pudieron cargar las dianas moleculares');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [plantId]);

  if (loading) {
    return <p style={styles.helperText}>Cargando dianas moleculares…</p>;
  }
  if (error) {
    return <p style={styles.errorText}>{error}</p>;
  }
  if (!data) return null;

  const hasTargets = data.targets && data.targets.length > 0;

  return (
    <div style={styles.container}>
      {/* Top: phytochemistry constellation (Mantis-inspired UX) */}
      <PhytochemistryMap plantId={plantId} />

      <hr style={styles.divider} />

      <div style={styles.intro}>
        <h3 style={styles.title}>Estructuras 3D de las proteínas diana</h3>
        <p style={styles.subtitle}>
          Cada estructura proviene de{' '}
          <a
            href="https://alphafold.ebi.ac.uk/"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.link}
          >
            AlphaFold (DeepMind · EBI)
          </a>{' '}
          y la coloración corresponde a confianza pLDDT (azul = baja, rojo = alta).
        </p>
      </div>

      {!hasTargets ? (
        <p style={styles.helperText}>
          Aún no curamos dianas moleculares para esta categoría. Próximamente.
        </p>
      ) : (
        <>
          <div style={styles.targetsList}>
            {data.targets.map((target) => {
              const active = target.uniprot_id === selectedUniprot;
              return (
                <button
                  type="button"
                  key={target.uniprot_id}
                  onClick={() => setSelectedUniprot(target.uniprot_id)}
                  style={{ ...styles.targetButton, ...(active ? styles.targetButtonActive : {}) }}
                >
                  <span style={styles.targetName}>{target.name}</span>
                  <span style={styles.targetAccession}>{target.uniprot_id}</span>
                </button>
              );
            })}
          </div>

          {selectedUniprot && (
            <>
              <MolecularViewer uniprotId={selectedUniprot} />
              {data.targets.find((t) => t.uniprot_id === selectedUniprot) && (
                <p style={styles.rationale}>
                  {data.targets.find((t) => t.uniprot_id === selectedUniprot).rationale}
                </p>
              )}
            </>
          )}
        </>
      )}

      {data.active_compounds && data.active_compounds.length > 0 && (
        <div style={styles.compoundsSection}>
          <h4 style={styles.compoundsTitle}>Compuestos activos reportados</h4>
          <ul style={styles.compoundsList}>
            {data.active_compounds.map((compound, idx) => (
              <li key={idx}>{compound}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { display: 'flex', flexDirection: 'column', gap: 20 },
  divider: { border: 'none', borderTop: '1px solid #e5e7eb', margin: '4px 0' },
  intro: {},
  title: { margin: 0, fontSize: 18, fontWeight: 600, color: '#111827' },
  subtitle: { marginTop: 4, fontSize: 13, color: '#4b5563', lineHeight: 1.5 },
  link: { color: '#059669', textDecoration: 'underline' },
  targetsList: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  targetButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    background: '#f9fafb',
    cursor: 'pointer',
    fontSize: 13,
  },
  targetButtonActive: {
    background: '#ecfdf5',
    borderColor: '#10b981',
    boxShadow: '0 0 0 1px #10b981 inset',
  },
  targetName: { fontWeight: 600, color: '#111827' },
  targetAccession: { fontSize: 11, color: '#6b7280', marginTop: 2 },
  rationale: {
    fontSize: 13,
    color: '#374151',
    background: '#f3f4f6',
    padding: 12,
    borderRadius: 6,
    margin: 0,
  },
  compoundsSection: { marginTop: 8 },
  compoundsTitle: { margin: '0 0 8px 0', fontSize: 14, color: '#111827' },
  compoundsList: { margin: 0, paddingLeft: 18, color: '#374151', fontSize: 13 },
  helperText: { color: '#6b7280', fontStyle: 'italic' },
  errorText: { color: '#b91c1c' },
};

export default PlantMolecularTab;
